import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, type LinkProps, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { DiningTable } from '@/bindings/DiningTable'
import type { SaveTableRequest } from '@/bindings/SaveTableRequest'
import { Button } from '@/components/button'
import { buttonClass } from '@/components/button-class'
import { ConfirmDeleteButton } from '@/components/confirm-delete'
import { Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { PrintIcon } from '@/components/icons'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { backOfficeKeys, tableListQuery, tablesPath } from '@/features/back-office/api'
import type { QrKind } from '@/features/back-office/qr-print-search'
import { claim, request } from '@/lib/api'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { canManageBranch } from '@/lib/roles'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/tables'
const ROUTE_PATH = '/$restaurantSlug/$branchSlug/manage/tables'
const PRINT_PATH = '/$restaurantSlug/$branchSlug/manage/tables/print'

/** Mirrors `romi_domain::MAX_TABLE_NAME_LEN`: the name input's `maxLength`. */
export const MAX_TABLE_NAME = 40

interface Scope {
  restaurantSlug: string
  branchSlug: string
  restaurantId: string
  branchId: string
}

/** The branch-level signs, in the order the PRD lists them. */
const BRANCH_SIGNS: readonly Exclude<QrKind, 'table'>[] = ['restaurant', 'branch', 'take_away']

/**
 * Tables page (PRD, G3): the QR signs a branch prints (restaurant, branch, take-away) and its
 * dine-in tables, each with its own immutable code. Admin and above in the branch.
 */
export function BranchTablesPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const { page } = useSearch({ from: ROUTE_ID })

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('tables.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (!canManageBranch(context.role)) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('tables.adminOnly')}
        </p>
      </Page>
    )
  }

  const scope: Scope = { restaurantSlug, branchSlug, restaurantId, branchId }
  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <p className="text-muted text-sm">{t('tables.hint')}</p>
      <SignsCard scope={scope} />
      <TablesCard scope={scope} page={page} />
    </Page>
  )
}

function PrintLink({ scope, table, name }: { scope: Scope; table: string; name: string }) {
  const t = useT()
  return (
    <Link
      to={PRINT_PATH}
      params={{ restaurantSlug: scope.restaurantSlug, branchSlug: scope.branchSlug }}
      search={{ kind: 'table', table }}
      aria-label={t('tables.printFor', { name })}
      className="text-accent-ink hover:bg-accent-soft flex min-h-9 shrink-0 items-center gap-1 rounded-lg px-3 text-sm font-medium"
    >
      <PrintIcon className="size-4" />
      {t('tables.print')}
    </Link>
  )
}

function SignsCard({ scope }: { scope: Scope }) {
  const t = useT()
  return (
    <Card className="flex flex-col gap-4" aria-label={t('tables.qrTitle')}>
      <h2 className="text-lg font-semibold">{t('tables.qrTitle')}</h2>
      <div className="flex flex-wrap gap-2">
        {BRANCH_SIGNS.map((kind) => (
          <Link
            key={kind}
            to={PRINT_PATH}
            params={{ restaurantSlug: scope.restaurantSlug, branchSlug: scope.branchSlug }}
            search={{ kind }}
            className={buttonClass('secondary')}
          >
            <PrintIcon className="size-4" />
            {t(`tables.printQr.${kind}`)}
          </Link>
        ))}
      </div>
    </Card>
  )
}

/** Every table page and single-table entry of the branch refetches; totals shift on every write. */
function useTablesInvalidation({ restaurantId, branchId }: Scope) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: backOfficeKeys.tables(restaurantId, branchId) })
}

function TablesCard({ scope, page }: { scope: Scope; page: number }) {
  const t = useT()
  const list = useQuery(tableListQuery(scope.restaurantId, scope.branchId, page))
  const link = (next: number): LinkProps => ({ to: ROUTE_PATH, params: scope, search: { page: next } })
  useClampedPage(page, list.data, link)
  const [editing, setEditing] = useState<string | undefined>()
  const [status, setStatus] = useState<'saved' | 'deleted' | undefined>()

  return (
    <Card className="flex flex-col gap-4" aria-label={t('tables.listTitle')}>
      <h2 className="text-lg font-semibold">{t('tables.listTitle')}</h2>
      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data && list.data.items.length === 0 && <p className="text-muted text-sm">{t('tables.empty')}</p>}
      {list.data && list.data.items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {list.data.items.map((table) => (
            <li key={table.id} className="border-border flex flex-col gap-3 rounded-lg border p-3">
              {editing === table.id ? (
                <TableForm
                  scope={scope}
                  table={table}
                  onSaved={() => {
                    setEditing(undefined)
                    setStatus('saved')
                  }}
                  onCancel={() => setEditing(undefined)}
                />
              ) : (
                <TableRow
                  scope={scope}
                  table={table}
                  onEdit={() => {
                    setStatus(undefined)
                    setEditing(table.id)
                  }}
                  onDeleted={() => setStatus('deleted')}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {list.data && (
        <Pager
          label={t('tables.pager')}
          page={page}
          total={Number(list.data.total)}
          link={link}
        />
      )}
      <TableForm
        scope={scope}
        onSaved={() => {
          setEditing(undefined)
          setStatus('saved')
        }}
      />
      {status && (
        <p role="status" className="text-muted text-sm">
          {t(status === 'saved' ? 'tables.saved' : 'tables.deleted')}
        </p>
      )}
    </Card>
  )
}

function TableRow({ scope, table, onEdit, onDeleted }: { scope: Scope; table: DiningTable; onEdit: () => void; onDeleted: () => void }) {
  const t = useT()
  const invalidate = useTablesInvalidation(scope)
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate font-medium">{table.name}</span>
        <span className="text-muted font-mono text-xs">{t('tables.code', { code: table.code })}</span>
      </span>
      <span className="flex items-center gap-1">
        <PrintLink scope={scope} table={table.id} name={table.name} />
        <Button size="sm" variant="ghost" aria-label={`${t('common.edit')}: ${table.name}`} onClick={onEdit}>
          {t('common.edit')}
        </Button>
        <ConfirmDeleteButton
          label={table.name}
          confirmLabel={t('tables.confirmDeleteOf', { name: table.name })}
          remove={() => request<void>(tablesPath(scope.restaurantId, scope.branchId, `/${table.id}`), { method: 'DELETE' })}
          onDeleted={() => {
            void invalidate()
            onDeleted()
          }}
        />
      </span>
    </div>
  )
}

/** Add (no `table`) or rename; the code never changes, so the row keeps its printed QR. */
function TableForm({ scope, table, onSaved, onCancel }: { scope: Scope; table?: DiningTable; onSaved: () => void; onCancel?: () => void }) {
  const t = useT()
  const invalidate = useTablesInvalidation(scope)
  const [name, setName] = useState(table?.name ?? '')

  const save = useMutation({
    mutationFn: () => {
      const body: SaveTableRequest = { name: name.trim() }
      const path = tablesPath(scope.restaurantId, scope.branchId, table ? `/${table.id}` : '')
      return request<DiningTable>(path, { method: table ? 'PUT' : 'POST', body })
    },
    onSuccess: async () => {
      await invalidate()
      if (!table) setName('')
      onSaved()
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const nameError = claim(save.error, 'INVALID_TABLE_NAME', 'TABLE_NAME_TAKEN')
  const label = table ? t('tables.editFor', { name: table.name }) : t('tables.add')
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label={label}>
      <Field label={t('tables.name')} hint={t('tables.nameHint', { max: MAX_TABLE_NAME })} error={nameError}>
        <Input
          value={name}
          maxLength={MAX_TABLE_NAME}
          required
          autoFocus={table !== undefined}
          onChange={(e) => {
            if (save.error) save.reset()
            setName(e.target.value)
          }}
        />
      </Field>
      <FormError error={nameError ? undefined : save.error} />
      <div className="flex gap-2">
        <Button type="submit" loading={save.isPending}>
          {table ? t('common.save') : t('tables.add')}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </form>
  )
}
